import { Box, Text, Toast, useToast } from '@zillow/constellation-10';
import { RentPriceGraph } from './Graphs/RentPriceGraph';
import { SingleFamilyRentGraph } from './Graphs/SingleFamilyRentGraph';
import { VacancyRatesGraph } from './Graphs/VacancyRatesGraph';
import { VacantUnitsGraph } from './Graphs/VacantUnitsGraph';
import { useMainLayout } from '../../../layouts/MainPageLayout/MainLayoutContext';
import { isAllSingleFamily } from '../../../../utils/isAllSingleFamily';
import { useCallback, useMemo, useState } from 'react';
import { CustomerAnalyticsPage } from '../../../SharedComponents/CustomerAnalyticsPage';
import { MarketDataReportSidebar } from './MarketDataReportSidebar';
import type { FileType, SelectedGraphs } from '../../../SharedComponents/ExportDialog';
import { extractMarketData, getMarketDataGraphConfigs } from './MarketDataReport.export';
import { useRentalManagerUser } from '@zg-rentals/rental-manager-env';
import { useQuery } from '@zg-rentals/react-query5';
import {
  MoodysAnalyticsWebApiHandler_getMoodysAnalyticsForRegion,
  MoodysAnalyticsWebApiHandler_getMoodysAnalyticsForRegion_Path,
  ZgSearchHomeType,
} from '@zg-rentals/ts-customer-analytics-client';
import { downloadExcel } from '../../../../utils/exportUtils';
import { downloadPDF } from '../../../../utils/pdfExportUtils';
import { captureGraphs } from '../../../../utils/captureGraphs';
import { buildCoverPageData } from '../../../../utils/buildCoverPageData';
import { emitMarketDataExportEvent, emitMarketDataExportPdfEvent } from '../../../../analytics/events';
import { useMarketDataFilters } from './MarketDataReport.hooks';
import { useGetMarketData } from './MarketDataReport.utils';
import { generateRentPriceData, generateVacancyRatesData, generateVacantUnitsData } from './MarketDataReport.data';
import {
  AverageRentCompsGraph,
  CostPerSqFtCompsGraph,
  generateCostPerSqFtChartData,
  generateRentCompsChartData,
  generateViewCompsChartData,
  PropertyViewCompsGraph,
  useMetricCompsData,
  usePriceCompsData,
  useRentCompsFilters,
} from './Graphs/RentComps';
import { numBedsToAnalyticsNumBedroomType } from './Graphs/RentComps/RentComps.utils';
import { EditCompetitorsModal } from './Graphs/RentComps/EditCompetitorsModal';

export const MarketDataReport = () => {
  const { filteredProperties, selectedRegionId, selectedMarketId, selectedFilters, regionsMapping } = useMainLayout();
  const { user } = useRentalManagerUser();
  const { enqueueToast } = useToast();
  const isImpersonator = Boolean(user?.impersonatorUserToken || user?.impersonatorEmail);

  const { mfBedsFilter, setMfBedsFilter, sfBedsFilter, setSfBedsFilter, vacantUnitsFilter, setVacantUnitsFilter } =
    useMarketDataFilters();

  const allSingleFamily = isAllSingleFamily(filteredProperties);
  const isAnyPropertySingleFamily = filteredProperties.some((property) => property.aggregateStats.isSingleFamily);

  const {
    data: moodysData,
    isLoading: isMoodysLoading,
    error: moodysDataError,
  } = useQuery({
    queryKey: [MoodysAnalyticsWebApiHandler_getMoodysAnalyticsForRegion_Path, { zillowRegionIds: [selectedMarketId] }],
    queryFn: () =>
      MoodysAnalyticsWebApiHandler_getMoodysAnalyticsForRegion({
        lookbackQuarters: 8,
        zillowRegionIds: [selectedMarketId],
      }),
    enabled: Boolean(selectedMarketId),
  });

  const hasMoodysData = !moodysDataError && !!moodysData && moodysData.length > 0;

  const { data: mfMarketAvgData, isLoading: isMfMarketAvgLoading } = useGetMarketData({
    regionId: selectedMarketId,
    bedsFilter: mfBedsFilter,
  });

  const { data: sfMarketAvgData, isLoading: isSfMarketAvgLoading } = useGetMarketData({
    regionId: selectedMarketId,
    bedsFilter: sfBedsFilter,
    propertyType: ZgSearchHomeType.SINGLE_FAMILY,
  });

  const rentPriceChartData = useMemo(
    () =>
      generateRentPriceData({
        filteredProperties,
        isSingleFamily: false,
        bedsFilter: mfBedsFilter,
        marketAvgData: mfMarketAvgData,
      }),
    [filteredProperties, mfBedsFilter, mfMarketAvgData],
  );

  const sfRentChartData = useMemo(
    () =>
      generateRentPriceData({
        filteredProperties,
        isSingleFamily: true,
        bedsFilter: sfBedsFilter,
        marketAvgData: sfMarketAvgData,
      }),
    [filteredProperties, sfBedsFilter, sfMarketAvgData],
  );

  const vacancyRatesChartData = useMemo(
    () =>
      generateVacancyRatesData({
        filteredProperties,
        moodysData,
      }),
    [filteredProperties, moodysData],
  );

  const vacantUnitsChartData = useMemo(
    () =>
      generateVacantUnitsData({
        filteredProperties,
        moodysData,
      }),
    [filteredProperties, moodysData],
  );

  const {
    selectedUnitId: compsUnitId,
    setSelectedUnitId: setCompsUnitId,
    selectedPropertyName: compsPropertyName,
    regionId: compsRegionId,
    numBedrooms: compsNumBedrooms,
    setNumBedrooms: setCompsNumBedrooms,
    propertyOptions: compsPropertyOptions,
  } = useRentCompsFilters(filteredProperties);

  const {
    priceComps,
    isLoading: isPriceCompsLoading,
    allCompNames,
    availableCompNames,
    setSelectedCompNames,
    allCompsColorMapping,
    selectedListingName,
  } = usePriceCompsData({
    unitId: compsUnitId,
    numBedrooms: compsNumBedrooms,
    regionId: compsRegionId,
  });

  const { metricComps, isLoading: isMetricCompsLoading } = useMetricCompsData({ unitId: compsUnitId });

  const compsBedsFilter = numBedsToAnalyticsNumBedroomType(compsNumBedrooms);
  const { data: compsMarketAvgData, isLoading: isCompsMarketAvgLoading } = useGetMarketData({
    regionId: compsRegionId,
    bedsFilter: compsBedsFilter,
  });

  const { chartData: rentCompsChartData, averages: rentCompsAverages } = useMemo(
    () => generateRentCompsChartData(priceComps, compsMarketAvgData, allCompNames),
    [priceComps, compsMarketAvgData, allCompNames],
  );

  const { chartData: costPerSqFtChartData, averages: costPerSqFtAverages } = useMemo(
    () => generateCostPerSqFtChartData(priceComps, allCompNames),
    [priceComps, allCompNames],
  );

  const { chartData: viewCompsChartData, averageViewRanking } = useMemo(
    () => generateViewCompsChartData(metricComps, allCompNames),
    [metricComps, allCompNames],
  );

  const [isEditCompetitorsOpen, setIsEditCompetitorsOpen] = useState(false);
  const handleEditCompetitors = useCallback(() => {
    setIsEditCompetitorsOpen(true);
  }, []);

  const graphConfigs = getMarketDataGraphConfigs(filteredProperties);

  const handleExport = async (fileType: FileType, selectedGraphs: SelectedGraphs) => {
    if (user) {
      const emitMap = { xls: emitMarketDataExportEvent, pdf: emitMarketDataExportPdfEvent } as const;
      emitMap[fileType]?.({ user });
    }

    const preComputedData = {
      rentPriceChartData,
      sfRentChartData,
      vacancyRatesChartData,
      vacantUnitsChartData,
      mfBedsFilter,
      sfBedsFilter,
      hasSingleFamilyProperties: isAnyPropertySingleFamily,
      rentCompsChartData,
      costPerSqFtChartData,
      viewCompsChartData,
      allCompNames,
      selectedListingName,
    };

    try {
      switch (fileType) {
        case 'xls': {
          const graphsData = extractMarketData(selectedGraphs, preComputedData);
          if (graphsData.length === 0) {
            enqueueToast(
              <Toast tone="critical">
                <Text>No data to export.</Text>
              </Toast>,
            );
            return;
          }
          await downloadExcel(graphsData, 'Market_Data');
          break;
        }
        case 'pdf': {
          const graphImages = await captureGraphs('Market Data', selectedGraphs);
          const tableData = extractMarketData(selectedGraphs, preComputedData);

          const pdfGraphPages = graphImages.map((graph) => {
            const normalizedGraphName = graph.name.toLowerCase().replace(/^total\s+/, '');
            const matchingTable = tableData.find((td) => {
              const normalizedTableName = td.name
                .toLowerCase()
                .replace(/_/g, ' ')
                .replace(/\s+(multifamily|single family)$/, '');
              return (
                normalizedTableName === normalizedGraphName ||
                normalizedTableName.startsWith(normalizedGraphName) ||
                normalizedGraphName.startsWith(normalizedTableName)
              );
            });
            return {
              name: graph.name,
              imageData: graph.imageData,
              tableData: matchingTable,
              legend: graph.legend,
              description: graph.description,
            };
          });

          if (pdfGraphPages.length === 0) {
            enqueueToast(
              <Toast tone="critical">
                <Text>No data to export.</Text>
              </Toast>,
            );
            return;
          }
          const coverPageData = await buildCoverPageData(
            'Market data',
            selectedFilters,
            regionsMapping,
            filteredProperties,
          );
          await downloadPDF(pdfGraphPages, 'Market Data', [], coverPageData);
          break;
        }
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to export data.';
      enqueueToast(
        <Toast tone="critical">
          <Text>{errorMessage}</Text>
        </Toast>,
      );
      throw error;
    }
  };

  return (
    <CustomerAnalyticsPage
      header="Market data"
      subheader="Source: Zillow internal data"
      sidebarTitle="Market highlights"
      sidebar={<MarketDataReportSidebar />}
      tabName="Market Data"
      graphConfigs={graphConfigs}
      onExport={handleExport}
    >
      <Box css={{ display: 'flex', flexDirection: 'column', gap: '800' }}>
        {!allSingleFamily && (
          <RentPriceGraph
            selectedRegionId={selectedRegionId}
            chartData={rentPriceChartData}
            isLoading={isMfMarketAvgLoading}
            bedsFilter={mfBedsFilter}
            setBedsFilter={setMfBedsFilter}
          />
        )}
        {isAnyPropertySingleFamily && (
          <SingleFamilyRentGraph
            selectedRegionId={selectedRegionId}
            chartData={sfRentChartData}
            isLoading={isSfMarketAvgLoading}
            bedsFilter={sfBedsFilter}
            setBedsFilter={setSfBedsFilter}
          />
        )}
        {!allSingleFamily && (
          <VacancyRatesGraph
            selectedRegionId={selectedRegionId}
            chartData={vacancyRatesChartData}
            isLoading={isMoodysLoading}
            hasMoodysData={hasMoodysData}
          />
        )}
        {!allSingleFamily && (
          <VacantUnitsGraph
            selectedRegionId={selectedRegionId}
            chartData={vacantUnitsChartData}
            isLoading={isMoodysLoading}
            buttonGroupFilter={vacantUnitsFilter}
            setButtonGroupFilter={setVacantUnitsFilter}
            hasMoodysData={hasMoodysData}
          />
        )}
        {!allSingleFamily && compsUnitId && !isImpersonator && (
          <>
            <AverageRentCompsGraph
              chartData={rentCompsChartData}
              averages={rentCompsAverages}
              isLoading={isPriceCompsLoading || isCompsMarketAvgLoading}
              selectedPropertyName={selectedListingName ?? compsPropertyName}
              allCompNames={allCompNames}
              allCompsColorMapping={allCompsColorMapping}
              numBedrooms={compsNumBedrooms}
              setNumBedrooms={setCompsNumBedrooms}
              propertyOptions={compsPropertyOptions}
              selectedUnitId={compsUnitId}
              setSelectedUnitId={setCompsUnitId}
              onEditCompetitors={handleEditCompetitors}
            />
            <CostPerSqFtCompsGraph
              chartData={costPerSqFtChartData}
              averages={costPerSqFtAverages}
              isLoading={isPriceCompsLoading}
              selectedPropertyName={selectedListingName ?? compsPropertyName}
              allCompNames={allCompNames}
              allCompsColorMapping={allCompsColorMapping}
              numBedrooms={compsNumBedrooms}
              setNumBedrooms={setCompsNumBedrooms}
              propertyOptions={compsPropertyOptions}
              selectedUnitId={compsUnitId}
              setSelectedUnitId={setCompsUnitId}
              onEditCompetitors={handleEditCompetitors}
            />
            <PropertyViewCompsGraph
              chartData={viewCompsChartData}
              averageViewRanking={averageViewRanking}
              isLoading={isMetricCompsLoading}
              selectedPropertyName={selectedListingName ?? compsPropertyName}
              allCompNames={allCompNames}
              allCompsColorMapping={allCompsColorMapping}
              propertyOptions={compsPropertyOptions}
              selectedUnitId={compsUnitId}
              setSelectedUnitId={setCompsUnitId}
              onEditCompetitors={handleEditCompetitors}
            />
          </>
        )}
      </Box>
      <EditCompetitorsModal
        open={isEditCompetitorsOpen}
        onClose={() => setIsEditCompetitorsOpen(false)}
        availableCompNames={availableCompNames}
        selectedCompNames={allCompNames}
        onConfirm={setSelectedCompNames}
      />
    </CustomerAnalyticsPage>
  );
};
